import { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { MAP } from "@/constants/testIds";
import { CalendarIcon, Layers, X } from "lucide-react";
import { format } from "date-fns";
import { NEIGHBORHOODS, DC_WARDS } from "@/lib/neighborhoods";
import {
  VA_NEW_RIGOR_STANDARD,
  VA_RIGOR_BADGE_LABEL,
  VA_DATA_SCHOOL_YEAR,
} from "@/lib/virginiaSol";

const Dot = ({ color }) => (
  <span
    aria-hidden
    className="inline-block w-2.5 h-2.5 rounded-full"
    style={{ background: color }}
  />
);

function toIsoDate(d) {
  return d ? format(d, "yyyy-MM-dd") : "";
}

function parseIsoDate(s) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

export default function MapFilters({ filters, setFilters, layers, setLayers, showLiteracyLayer, setShowLiteracyLayer, showMdLiteracyLayer, setShowMdLiteracyLayer, showVaLiteracyLayer, setShowVaLiteracyLayer, showDcplLayer, setShowDcplLayer }) {
  const nbList = useMemo(() => NEIGHBORHOODS.map((n) => n.name), []);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const range = {
    from: parseIsoDate(filters.from_date),
    to: parseIsoDate(filters.to_date),
  };

  const onRangeChange = (next) => {
    setFilters({
      ...filters,
      from_date: toIsoDate(next?.from),
      to_date: toIsoDate(next?.to),
    });
  };

  const rangeLabel = () => {
    if (range.from && range.to) {
      return `${format(range.from, "LLL d")} – ${format(range.to, "LLL d, yyyy")}`;
    }
    if (range.from) return `${format(range.from, "LLL d, yyyy")} – …`;
    return "Pick a date range";
  };

  const clear = () => {
    setFilters({ jurisdiction: "ALL", ward: "ALL", neighborhood: "", from_date: "", to_date: "" });
    setLayers({ pending: true, scheduled: true, active: true });
  };

  return (
    <aside
      data-testid={MAP.filterPanel}
      className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-6"
    >
      <div className="flex items-center gap-2">
        <Layers size={16} className="text-stone-500" />
        <h3 className="font-serif text-lg font-semibold">Filters</h3>
      </div>

      {/* Layer toggles */}
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Pin states</div>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <Label htmlFor={MAP.toggleLayerActive} className="flex items-center gap-2 text-sm font-normal cursor-pointer">
              <Dot color="#22C55E" /> Installed
            </Label>
            <Switch
              id={MAP.toggleLayerActive}
              data-testid={MAP.toggleLayerActive}
              checked={layers.active}
              onCheckedChange={(v) => setLayers({ ...layers, active: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor={MAP.toggleLayerScheduled} className="flex items-center gap-2 text-sm font-normal cursor-pointer">
              <Dot color="#94A3B8" /> Scheduled
            </Label>
            <Switch
              id={MAP.toggleLayerScheduled}
              data-testid={MAP.toggleLayerScheduled}
              checked={layers.scheduled}
              onCheckedChange={(v) => setLayers({ ...layers, scheduled: v })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor={MAP.toggleLayerPending} className="flex items-center gap-2 text-sm font-normal cursor-pointer">
              <Dot color="#F59E0B" /> Pending
            </Label>
            <Switch
              id={MAP.toggleLayerPending}
              data-testid={MAP.toggleLayerPending}
              checked={layers.pending}
              onCheckedChange={(v) => setLayers({ ...layers, pending: v })}
            />
          </div>
        </div>
      </div>

      {/* Literacy data overlay */}
      {setShowLiteracyLayer && (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Map Overlays</div>
          <div className="flex items-start justify-between gap-3">
            <Label
              htmlFor="toggle-literacy"
              className="flex flex-col gap-1 text-sm font-normal cursor-pointer leading-tight"
            >
              <span className="flex items-center gap-2">
                <span aria-hidden className="inline-block w-3 h-3 rounded-sm" style={{ background: "linear-gradient(to right, #FFEBEE, #B71C1C)" }} />
                DC Ward Literacy
              </span>
              <span className="text-[11px] text-stone-500">DC CAPE · 2025</span>
            </Label>
            <Switch
              id="toggle-literacy"
              data-testid="toggle-literacy-layer"
              checked={!!showLiteracyLayer}
              onCheckedChange={(v) => setShowLiteracyLayer(v)}
            />
          </div>
          {setShowMdLiteracyLayer && (
            <div className="flex items-start justify-between gap-3">
              <Label
                htmlFor="toggle-md-literacy"
                className="flex flex-col gap-1 text-sm font-normal cursor-pointer leading-tight"
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden className="inline-block w-3 h-3 rounded-sm" style={{ background: "linear-gradient(to right, #FFEDED, #C62828)" }} />
                  Maryland ELA Literacy
                </span>
                <span className="text-[11px] text-stone-500">MCAP · 2024–25</span>
              </Label>
              <Switch
                id="toggle-md-literacy"
                data-testid="toggle-md-literacy-layer"
                checked={!!showMdLiteracyLayer}
                onCheckedChange={(v) => setShowMdLiteracyLayer(v)}
              />
            </div>
          )}
          {setShowVaLiteracyLayer && (
            <div className="flex items-start justify-between gap-3">
              <Label
                htmlFor="toggle-va-literacy"
                className="flex flex-col gap-1 text-sm font-normal cursor-pointer leading-tight"
              >
                <span className="flex items-center gap-2 flex-wrap">
                  <span aria-hidden className="inline-block w-3 h-3 rounded-sm" style={{ background: "linear-gradient(to right, #FFEDED, #C62828)" }} />
                  Virginia SOL Reading
                  {VA_NEW_RIGOR_STANDARD && (
                    <span
                      data-testid="va-rigor-badge-filter"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[9px] font-bold uppercase tracking-wider"
                    >
                      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {VA_RIGOR_BADGE_LABEL}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-stone-500">
                  VDOE · {VA_DATA_SCHOOL_YEAR}
                  {VA_NEW_RIGOR_STANDARD && " · not comparable to prior years"}
                </span>
              </Label>
              <Switch
                id="toggle-va-literacy"
                data-testid="toggle-va-literacy-layer"
                checked={!!showVaLiteracyLayer}
                onCheckedChange={(v) => setShowVaLiteracyLayer(v)}
              />
            </div>
          )}
          {setShowDcplLayer && (
            <div className="flex items-start justify-between gap-3">
              <Label
                htmlFor="toggle-dcpl"
                className="flex flex-col gap-1 text-sm font-normal cursor-pointer leading-tight"
              >
                <span className="flex items-center gap-2">
                  <span aria-hidden className="inline-grid place-items-center w-3.5 h-3.5 rounded-full" style={{ background: "#B89968" }}>
                    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </span>
                  DC Public Library Branches
                </span>
                <span className="text-[11px] text-stone-500">DCPL · 28 open branches</span>
              </Label>
              <Switch
                id="toggle-dcpl"
                data-testid="toggle-dcpl-layer"
                checked={!!showDcplLayer}
                onCheckedChange={(v) => setShowDcplLayer(v)}
              />
            </div>
          )}
        </div>
      )}

      {/* Jurisdiction */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Jurisdiction</Label>
        <Select
          value={filters.jurisdiction}
          onValueChange={(v) => setFilters({ ...filters, jurisdiction: v })}
        >
          <SelectTrigger data-testid={MAP.filterJurisdiction} className="rounded-xl">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All — DC, MD, VA</SelectItem>
            <SelectItem value="DC">Washington D.C.</SelectItem>
            <SelectItem value="MD">Maryland</SelectItem>
            <SelectItem value="VA">Virginia</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* DC Ward */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">DC Ward</Label>
        <Select
          value={filters.ward}
          onValueChange={(v) => setFilters({ ...filters, ward: v })}
        >
          <SelectTrigger data-testid={MAP.filterWard} className="rounded-xl">
            <SelectValue placeholder="All wards" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All wards</SelectItem>
            {DC_WARDS.map((w) => (
              <SelectItem key={w} value={w}>Ward {w}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Neighborhood search */}
      <div className="space-y-2">
        <Label htmlFor={MAP.searchNeighborhood} className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Neighborhood</Label>
        <Input
          id={MAP.searchNeighborhood}
          data-testid={MAP.searchNeighborhood}
          list="nb-list"
          placeholder="Search neighborhood…"
          value={filters.neighborhood}
          onChange={(e) => setFilters({ ...filters, neighborhood: e.target.value })}
          className="rounded-xl"
        />
        <datalist id="nb-list">
          {nbList.map((n) => <option key={n} value={n} />)}
        </datalist>
      </div>

      {/* Date range — calendar picker */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-[0.18em] text-stone-500 font-semibold">Date range</Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              data-testid={MAP.filterDateFrom}
              variant="outline"
              className={`w-full justify-start rounded-xl font-normal h-10 ${!range.from && "text-stone-500"}`}
            >
              <CalendarIcon size={14} className="mr-2 text-stone-500" />
              {rangeLabel()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={range.from}
              selected={range}
              onSelect={onRangeChange}
              numberOfMonths={1}
            />
            <div className="flex items-center justify-between gap-2 border-t border-stone-100 px-3 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRangeChange({ from: undefined, to: undefined })}
                className="text-xs"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => setCalendarOpen(false)}
                className="bg-[#122036] hover:bg-[#0B1626] text-white text-xs rounded-full px-4"
                data-testid={MAP.filterDateTo}
              >
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Button
        data-testid={MAP.clearFiltersBtn}
        onClick={clear}
        variant="outline"
        className="w-full rounded-full border-stone-200"
      >
        <X size={14} className="mr-1.5" /> Clear filters
      </Button>
    </aside>
  );
}
